import { HttpErrorResponse } from '@angular/common/http';
import { YaTenemosEstado } from '../../Models/ya-tenemos-datos-estado.enum';
import { saveToStorage, saveToStorageAsync } from '../../helpers/storageUtils';
import { RESUMEN_COMPRA, YATEMENOSDATOS } from '../../routes.constans';
import {
  CHECK_THE_INFORMATION_REDIRECT,
  CONTACT_THE_REGISTRY_REDIRECT,
  INFORMACION_FASECOLDA_REDIRECT,
  PROBLEMAS_TECNICOS_REDIRECT,
  SARLAFT_DENIED_REDIRECT,
} from '../final-pages/final-pages.constants';
import { SarlaftLogicResponse, SarlaftResponse } from '../interfaces/sarlaft.interface';

const RETRY_ATTEMPTS_KEY = 'sarlaftRetryAttempts';

function getRetryCount(): number {
  const attempts = sessionStorage.getItem(RETRY_ATTEMPTS_KEY);
  console.log(`Intentos de reintento Sarlaft: ${attempts}`);

  return attempts ? parseInt(attempts, 10) : 0;
}

function incrementRetryCount(): void {
  const currentCount = getRetryCount();
  sessionStorage.setItem(RETRY_ATTEMPTS_KEY, (currentCount + 1).toString());
}

/**
 * Procesa la respuesta o error del servicio Sarlaft y determina la acción a seguir.
 * @param response La respuesta exitosa del servicio (2XX).
 * @param error El error HTTP en caso de una respuesta no exitosa (4XX, 5XX).
 * @returns Un objeto con la ruta de redirección y una acción opcional.
 */
export async function processSarlaftResponse(
  response: SarlaftResponse | null,
  error: HttpErrorResponse | null,
): Promise<SarlaftLogicResponse | null> {
  // Regla 3: Errores 5XX
  if (error && error.status >= 500) {
    await saveToStorageAsync(
      'ya_tenemos_tus_datos',
      YaTenemosEstado.LEAD_ABANDONO_RESUMEN,
      'session',
    );
    return {
      redirectPath: [PROBLEMAS_TECNICOS_REDIRECT],
      action: 'UPDATE_LEAD',
      leadProspect: 'Falla conexión Sarlaft',
      leadPercentage: '80',
      estadoRetoma: 'Inhabilitado',
      estadoRescate: 'Habilitado',
    };
  }

  // Reglas 1 y 2: Errores 4XX
  if (error && error.status >= 400) {
    const messageError: SarlaftResponse = JSON.parse(error.error.message);
    const sarlaftEvaluation = messageError?.data?.sarlaftEvaluation;

    // Regla 1: Causa es "regla país"
    if (sarlaftEvaluation === 'AJUSTAR INFORMACION EN REGISTRADURIA') {
      const retryCount = getRetryCount();
      if (retryCount >= 2) {
        // Al 3er intento (0, 1, 2)
        return {
          redirectPath: [CONTACT_THE_REGISTRY_REDIRECT], action: 'UPDATE_LEAD',
          leadProspect: 'Falla Sarlaft registraduria',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
        };
      } else {
        incrementRetryCount();
        return {
          redirectPath: [CHECK_THE_INFORMATION_REDIRECT], action: 'UPDATE_LEAD',
          leadProspect: 'Falla Sarlaft registraduria',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
        };
      }
    } else if (sarlaftEvaluation.toUpperCase() === 'NO SE HA ENCONTRADO INFORMACION PARA LA PLACA SUMINISTRADA EN LA GUIA DE VALORES DE FASECOLDA') {
      return {
        redirectPath: [INFORMACION_FASECOLDA_REDIRECT], action: 'UPDATE_LEAD',
        leadProspect: 'Falla Sarlaft registraduria',
        leadPercentage: '80',
        estadoRetoma: 'Inhabilitado',
        estadoRescate: 'Inhabilitado',
      };
    } else {
      // Regla 2: Otros errores 4XX
      return {
        redirectPath: [PROBLEMAS_TECNICOS_REDIRECT],
        action: 'UPDATE_LEAD',
        leadProspect: 'Falla conexión Sarlaft',
        leadPercentage: '80',
        estadoRetoma: 'Inhabilitado',
        estadoRescate: 'Habilitado',
      };
    }
  }

  // Lógica para respuestas 2XX
  if (response) {
    const sarlaftEvaluation = response.data.sarlaftEvaluation;

    switch (sarlaftEvaluation) {
      // Regla 4
      case 'RESULTADO NO ESPERADO DE LA EVALUACIÓN':
        await saveToStorageAsync(
          'ya_tenemos_tus_datos',
          YaTenemosEstado.LEAD_ABANDONO_RESUMEN,
          'session',
        );
        return {
          redirectPath: [PROBLEMAS_TECNICOS_REDIRECT],
          action: 'UPDATE_LEAD',
          leadProspect: 'Resultado no esperado de Sarlaft',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
        };

      // Regla 5
      case 'SARLAFT RECHAZADO':
        return {
          redirectPath: [SARLAFT_DENIED_REDIRECT],
          action: 'UPDATE_LEAD',
          leadProspect: 'No pasa sarlaft',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
          leadStatus: '0_Descartado',
        };

      case 'NO SE PUEDE EXPEDIR POR ESTE MEDIO':
        return {
          redirectPath: [SARLAFT_DENIED_REDIRECT],
          action: 'UPDATE_LEAD',
          leadProspect: 'No pasa sarlaft',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
          leadStatus: '0_Descartado',
        };

      // Regla 6 y 8 (inicio del puller)
      case 'RESULTADO DE EVALUACIÓN PENDIENTE':
        await saveToStorageAsync(
          'ya_tenemos_tus_datos',
          YaTenemosEstado.LEAD_ABANDONO_RESUMEN,
          'session',
        );
        return {
          redirectPath: [YATEMENOSDATOS],
          action: 'UPDATE_LEAD',
          leadProspect: 'Falla Sarlaft registraduria',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
        };

      case 'ESPERA DE WEBHOOK':
        await saveToStorageAsync(
          'ya_tenemos_tus_datos',
          YaTenemosEstado.LEAD_ABANDONO_RESUMEN,
          'session',
        );
        return {
          redirectPath: [YATEMENOSDATOS],
          action: 'START_PULLER', // Usamos la acción para iniciar el puller
        };

      // Regla 7
      case 'SARLAFT APROBADO':
        return { redirectPath: [RESUMEN_COMPRA] };

      // Regla 8 (inicio del puller)
      case 'AJUSTAR INFORMACION EN REGISTRADURIA':
        const retryCount = getRetryCount();
        if (retryCount >= 2) {
          // Al 3er intento (0, 1, 2)
          return {
            redirectPath: [CONTACT_THE_REGISTRY_REDIRECT], action: 'UPDATE_LEAD',
            leadProspect: 'Falla Sarlaft registraduria',
            leadPercentage: '80',
            estadoRetoma: 'Inhabilitado',
            estadoRescate: 'Inhabilitado',
          };
        } else {
          incrementRetryCount();
          return {
            redirectPath: [CHECK_THE_INFORMATION_REDIRECT], action: 'UPDATE_LEAD',
            leadProspect: 'Falla Sarlaft registraduria',
            leadPercentage: '80',
            estadoRetoma: 'Inhabilitado',
            estadoRescate: 'Inhabilitado',
          };
        }

      default:
        // Caso no controlado, podría redirigir a una pantalla de error genérico.
        console.warn('Causa Sarlaft no manejada:', sarlaftEvaluation);
        return { redirectPath: [CHECK_THE_INFORMATION_REDIRECT] };
    }
  }

  // Si no hay response ni error, no debería ocurrir.
  return null;
}

/**
 * Versión síncrona de processSarlaftResponse para compatibilidad hacia atrás
 * DEPRECATED: Usar processSarlaftResponseAsync para nuevas implementaciones
 */
export function processSarlaftResponseSync(
  response: SarlaftResponse | null,
  error: HttpErrorResponse | null,
): SarlaftLogicResponse | null {
  // Regla 3: Errores 5XX
  if (error && error.status >= 500) {
    saveToStorage('ya_tenemos_tus_datos', YaTenemosEstado.LEAD_ABANDONO_RESUMEN, 'session');
    return {
      redirectPath: [PROBLEMAS_TECNICOS_REDIRECT],
      action: 'UPDATE_LEAD',
      leadProspect: 'Falla conexión Sarlaft',
      leadPercentage: '80',
      estadoRetoma: 'Inhabilitado',
      estadoRescate: 'Habilitado',
    };
  }

  // Reglas 1 y 2: Errores 4XX
  if (error && error.status >= 400) {
    const causaError = error.error?.causa;

    // Regla 1: Causa es "regla país"
    if (causaError === 'regla país') {
      const retryCount = getRetryCount();
      if (retryCount >= 2) {
        // Al 3er intento (0, 1, 2)
        return { redirectPath: [CONTACT_THE_REGISTRY_REDIRECT] };
      } else {
        incrementRetryCount();
        return { redirectPath: [CHECK_THE_INFORMATION_REDIRECT] };
      }
    } else {
      // Regla 2: Otros errores 4XX
      return { redirectPath: [CHECK_THE_INFORMATION_REDIRECT] };
    }
  }

  // Lógica para respuestas 2XX
  if (response) {
    const sarlaftEvaluation = response.data.sarlaftEvaluation;

    switch (sarlaftEvaluation) {
      // Regla 4
      case 'RESULTADO NO ESPERADO DE LA EVALUACIÓN':
        saveToStorage('ya_tenemos_tus_datos', YaTenemosEstado.LEAD_ABANDONO_RESUMEN, 'session');
        return {
          redirectPath: [PROBLEMAS_TECNICOS_REDIRECT],
          action: 'UPDATE_LEAD',
          leadProspect: 'Falla conexión Sarlaft',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Habilitado',
        };

      // Regla 5
      case 'SARLAFT RECHAZADO':
        return {
          redirectPath: [SARLAFT_DENIED_REDIRECT],
          action: 'UPDATE_LEAD',
          leadProspect: 'No pasa sarlaft',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
          leadStatus: '0_Descartado',
        };

      case 'NO SE PUEDE EXPEDIR POR ESTE MEDIO':
        return {
          redirectPath: [SARLAFT_DENIED_REDIRECT],
          action: 'UPDATE_LEAD',
          leadProspect: 'No pasa sarlaft',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
          leadStatus: '0_Descartado',
        };

      // Regla 6 y 8 (inicio del puller)
      case 'RESULTADO DE EVALUACIÓN PENDIENTE':
        saveToStorage('ya_tenemos_tus_datos', YaTenemosEstado.LEAD_ABANDONO_RESUMEN, 'session');
        return {
          redirectPath: [YATEMENOSDATOS],
          action: 'UPDATE_LEAD',
          leadProspect: 'Falla Sarlaft registraduria',
          leadPercentage: '80',
          estadoRetoma: 'Inhabilitado',
          estadoRescate: 'Inhabilitado',
        };

      case 'ESPERA DE WEBHOOK':
        saveToStorage('ya_tenemos_tus_datos', YaTenemosEstado.LEAD_ABANDONO_RESUMEN, 'session');
        return {
          redirectPath: [YATEMENOSDATOS],
          action: 'START_PULLER',
        };

      // Regla 7
      case 'SARLAFT APROBADO':
        return { redirectPath: [RESUMEN_COMPRA] };

      // Regla 8 (inicio del puller)
      case 'AJUSTAR INFORMACION EN REGISTRADURIA':
        const retryCount = getRetryCount();
        if (retryCount >= 2) {
          // Al 3er intento (0, 1, 2)
          return {
            redirectPath: [CONTACT_THE_REGISTRY_REDIRECT], action: 'UPDATE_LEAD',
            leadProspect: 'Falla Sarlaft registraduria',
            leadPercentage: '80',
            estadoRetoma: 'Inhabilitado',
            estadoRescate: 'Inhabilitado',
          };
        } else {
          incrementRetryCount();
          return { redirectPath: [CHECK_THE_INFORMATION_REDIRECT] };
        }

      default:
        console.warn('Causa Sarlaft no manejada:', sarlaftEvaluation);
        return { redirectPath: [CHECK_THE_INFORMATION_REDIRECT] };
    }
  }

  return null;
}

/**
 * Versión asíncrona recomendada de processSarlaftResponse
 * Asegura que todos los valores se guarden correctamente antes de continuar
 */
export async function processSarlaftResponseAsync(
  response: SarlaftResponse | null,
  error: HttpErrorResponse | null,
): Promise<SarlaftLogicResponse | null> {
  return processSarlaftResponse(response, error);
}

/**
 * Función utilitaria que procesa la respuesta Sarlaft y ejecuta la navegación de manera segura
 * @param response Respuesta del servicio Sarlaft
 * @param error Error HTTP si existe
 * @param router Router de Angular para navegación
 * @param updateLeadCallback Callback para actualizar el lead si es necesario
 * @param startPollingCallback Callback para iniciar polling si es necesario
 * @returns Promise que se resuelve cuando se completa el procesamiento
 */
export async function processSarlaftAndNavigate(
  response: SarlaftResponse | null,
  error: HttpErrorResponse | null,
  router: any,
  updateLeadCallback?: () => void,
  startPollingCallback?: (response: SarlaftResponse) => void,
): Promise<void> {
  try {
    const decision = await processSarlaftResponse(response, error);

    if (!decision) {
      return;
    }

    // Si la decisión requiere actualizar el lead, lo hacemos
    if (decision.action === 'UPDATE_LEAD' && updateLeadCallback) {
      updateLeadCallback();
    }

    // Si la decisión requiere iniciar el polling
    if (decision.action === 'START_PULLER' && response && startPollingCallback) {
      console.log('Respuesta inicial indica polling. Iniciando sondeo...');
      if (updateLeadCallback) {
        updateLeadCallback();
      }
      startPollingCallback(response);
      return; // El polling se encargará del resto
    }

    // Para todos los demás casos, navegamos
    if (decision.redirectPath.length > 0) {
      router.navigate(decision.redirectPath);
    }
  } catch (storageError) {
    console.error('Error al procesar respuesta Sarlaft:', storageError);
    // En caso de error, navegamos a una página de error
    router.navigate([CHECK_THE_INFORMATION_REDIRECT]);
  }
}
